import { createBrowserRouter, RouterProvider, Navigate, useParams } from "react-router";

import 'assets/reset.css'
import { useWebview } from "hooks/useWebview";
import { AlertProvider } from "contexts/AlertContext";
import { ProtectedRoute } from "components/common/ProtectedRoute";
import { Onboarding } from "pages/onboarding";
import { SignUp } from "pages/signUp";
import { Login } from "pages/login";
import { Home } from "pages/home";
import { Functions } from "pages/functions";
import { MeetingTypeSelect } from "pages/meeting/create/MeetingTypeSelect";
import { MeetingCreateStep1 } from "pages/meeting/create/Step1";
import { MeetingCreateStep2 } from "pages/meeting/create/Step2";
import { TravelPlanStep } from "pages/meeting/create/TravelPlanStep";
import { TravelDestination } from "pages/meeting/create/TravelDestination";
import { PlaceSearch } from "pages/meeting/create/PlaceSearch";
import { TimePicker } from "pages/meeting/create/TimePicker";
import { MeetingShare } from "pages/meeting/create/MeetingShare";
import { TravelMeetingShare } from "pages/meeting/create/TravelMeetingShare";
import { TravelRecommendations } from "pages/meeting/create/TravelRecommendations";
import { JoinWithCode } from "pages/meeting/JoinWithCode";
import { MyMeetingsList } from "pages/meeting/MyMeetingsList";
import { MeetingDetail } from "pages/meeting/MeetingDetail";
import { MeetingMap } from "pages/meeting/MeetingMap";
import { RealtimeLocation } from "pages/meeting/RealtimeLocation";
import { MeetingEdit } from "pages/meeting/MeetingEdit";
import { InvitationEntry } from "pages/invitation/InvitationEntry";
import { InvitationInfo } from "pages/invitation/InvitationInfo";
import { InvitationLocation } from "pages/invitation/InvitationLocation";
import { InvitationResult } from "pages/invitation/InvitationResult";
import { ScheduleMain } from "pages/schedule/ScheduleMain";
import { ScheduleInput } from "pages/schedule/ScheduleInput";
import { ScheduleVote } from "pages/schedule/ScheduleVote";
import { ScheduleResult } from "pages/schedule/ScheduleResult";
import { ScheduleSelectMeeting } from "pages/schedule/ScheduleSelectMeeting";
import { CostInput } from "pages/cost/CostInput";
import { CostReceipt } from "pages/cost/CostReceipt";
import { CostResult } from "pages/cost/CostResult";
import { CostShare } from "pages/cost/CostShare";
import { ChatEmbed } from "pages/chat";
import { Settings } from "pages/settings";
import { Profile } from "pages/settings/Profile";
import { BlockedUsers } from "pages/settings/BlockedUsers";
import { FAQ } from "pages/settings/FAQ";
import { Inquiry } from "pages/settings/Inquiry";
import { Terms } from "pages/settings/Terms";
import { Notifications } from "pages/settings/Notifications";
import { ChangePassword } from "pages/settings/ChangePassword";
import { TermDetail } from "pages/settings/TermDetail";
import { Notice } from "pages/settings/Notice";
import { NoticeDetail } from "pages/settings/NoticeDetail";
import { InquiryList } from "pages/settings/InquiryList";
import { MeetingsList } from "pages/meeting/MeetingsList";

// /meeting/:meetingId를 /meeting/detail/:meetingId로 리다이렉트하는 컴포넌트
const MeetingDetailRedirect = () => {
    const { meetingId } = useParams<{ meetingId: string }>();
    return <Navigate to={`/meeting/detail/${meetingId}`} replace />;
};

const router = createBrowserRouter([
    // ===== Public Routes (인증 불필요) =====
    { path: "/onboarding", element: <Onboarding /> },
    { path: "/login", element: <Login /> },
    { path: "/sign-up", element: <SignUp /> },
    // 초대 링크는 로그인 없이도 볼 수 있도록 (초대 정보 확인 후 로그인 유도)
    { path: "/invitation/:inviteCode", element: <InvitationEntry /> },
    { path: "/invitation/info/:inviteCode", element: <InvitationInfo /> },
    { path: "/invitation/location/:inviteCode", element: <InvitationLocation /> },
    { path: "/invitation/result/:inviteCode", element: <InvitationResult /> },

    // ===== Protected Routes (인증 필요) =====
    { path: "/", element: <ProtectedRoute><Home /></ProtectedRoute> },
    { path: "/home", element: <ProtectedRoute><Home /></ProtectedRoute> },
    { path: "/functions", element: <ProtectedRoute><Functions /></ProtectedRoute> },

    // 모임 생성
    { path: "/meeting/create", element: <ProtectedRoute><MeetingTypeSelect /></ProtectedRoute> },
    { path: "/meeting/create/step1", element: <ProtectedRoute><MeetingTypeSelect /></ProtectedRoute> },
    { path: "/meeting/create/step2", element: <ProtectedRoute><MeetingCreateStep1 /></ProtectedRoute> },
    { path: "/meeting/create/step3", element: <ProtectedRoute><MeetingCreateStep2 /></ProtectedRoute> },
    { path: "/meeting/create/travel", element: <ProtectedRoute><TravelPlanStep /></ProtectedRoute> },
    { path: "/meeting/create/travel-destination", element: <ProtectedRoute><TravelDestination /></ProtectedRoute> },
    { path: "/meeting/place-search", element: <ProtectedRoute><PlaceSearch /></ProtectedRoute> },
    { path: "/meeting/time-picker", element: <ProtectedRoute><TimePicker /></ProtectedRoute> },
    { path: "/meeting/share", element: <ProtectedRoute><MeetingShare /></ProtectedRoute> },
    { path: "/meeting/travel-share", element: <ProtectedRoute><TravelMeetingShare /></ProtectedRoute> },
    { path: "/meeting/travel-recommendations", element: <ProtectedRoute><TravelRecommendations /></ProtectedRoute> },

    // 모임 목록 및 상세
    { path: "/join-code", element: <ProtectedRoute><JoinWithCode /></ProtectedRoute> },
    { path: "/my-meetings", element: <ProtectedRoute><MyMeetingsList /></ProtectedRoute> },
    { path: "/meetings", element: <ProtectedRoute><MeetingsList /></ProtectedRoute> },
    { path: "/meeting/detail/:meetingId", element: <ProtectedRoute><MeetingDetail /></ProtectedRoute> },
    { path: "/meeting/:meetingId/map", element: <ProtectedRoute><MeetingMap /></ProtectedRoute> },
    { path: "/meeting/location/:meetingId", element: <ProtectedRoute><RealtimeLocation /></ProtectedRoute> },
    { path: "/meeting/edit/:meetingId", element: <ProtectedRoute><MeetingEdit /></ProtectedRoute> },
    { path: "/meeting/:meetingId", element: <ProtectedRoute><MeetingDetailRedirect /></ProtectedRoute> },

    // 일정
    { path: "/schedule/select", element: <ProtectedRoute><ScheduleSelectMeeting /></ProtectedRoute> },
    { path: "/schedule/main/:meetingId", element: <ProtectedRoute><ScheduleMain /></ProtectedRoute> },
    { path: "/schedule/input/:meetingId", element: <ProtectedRoute><ScheduleInput /></ProtectedRoute> },
    { path: "/schedule/vote/:meetingId", element: <ProtectedRoute><ScheduleVote /></ProtectedRoute> },
    { path: "/schedule/result/:meetingId", element: <ProtectedRoute><ScheduleResult /></ProtectedRoute> },

    // 정산
    { path: "/cost/:meetingId", element: <ProtectedRoute><CostInput /></ProtectedRoute> },
    { path: "/cost/receipt/:meetingId", element: <ProtectedRoute><CostReceipt /></ProtectedRoute> },
    { path: "/cost/result/:meetingId", element: <ProtectedRoute><CostResult /></ProtectedRoute> },
    { path: "/cost/share/:meetingId", element: <ProtectedRoute><CostShare /></ProtectedRoute> },

    // 채팅
    { path: "/chat", element: <ProtectedRoute><ChatEmbed /></ProtectedRoute> },
    { path: "/chat/*", element: <ProtectedRoute><ChatEmbed /></ProtectedRoute> },

    // 설정
    { path: "/settings", element: <ProtectedRoute><Settings /></ProtectedRoute> },
    { path: "/settings/profile", element: <ProtectedRoute><Profile /></ProtectedRoute> },
    { path: "/settings/blocked-users", element: <ProtectedRoute><BlockedUsers /></ProtectedRoute> },
    { path: "/settings/notice", element: <ProtectedRoute><Notice /></ProtectedRoute> },
    { path: "/settings/notice/:noticeId", element: <ProtectedRoute><NoticeDetail /></ProtectedRoute> },
    { path: "/settings/faq", element: <ProtectedRoute><FAQ /></ProtectedRoute> },
    { path: "/settings/inquiry", element: <ProtectedRoute><Inquiry /></ProtectedRoute> },
    { path: "/settings/inquiry-list", element: <ProtectedRoute><InquiryList /></ProtectedRoute> },
    { path: "/settings/terms", element: <ProtectedRoute><Terms /></ProtectedRoute> },
    { path: "/settings/terms/:termId", element: <ProtectedRoute><TermDetail /></ProtectedRoute> },
    { path: "/settings/notifications", element: <ProtectedRoute><Notifications /></ProtectedRoute> },
    { path: "/settings/change-password", element: <ProtectedRoute><ChangePassword /></ProtectedRoute> }
])

export default function Router() {

    useWebview();

    return (
        <AlertProvider>
            <RouterProvider router={router} />
        </AlertProvider>
    );
}